/**
 * Unified status color tokens for Chip / Badge components.
 * Use these instead of hardcoded hex values so that all status
 * labels across admin and biobank pages stay visually consistent.
 *
 * Standard chip shape: borderRadius "6px", height "24px", fontSize "0.75rem".
 *
 * Usage:
 *   import { STATUS_CHIP_STYLES, BASE_CHIP_SX } from "constants/statusColors";
 *
 *   <Chip
 *     label="Active"
 *     size="small"
 *     sx={{ ...BASE_CHIP_SX, ...STATUS_CHIP_STYLES.success }}
 *   />
 */

/** Base sx applied to every status Chip for consistent shape and typography. */
export const BASE_CHIP_SX = {
  borderRadius: "16px",
  height: "24px",
  fontSize: "12px",
  fontWeight: 600,
  fontFamily: "'Brand Fonts', sans-serif",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  border: "none",
  cursor: "default",
  pointerEvents: "none",
};

/**
 * Semantic color variants.
 * Each entry provides backgroundColor, color (text), and border.
 */
export const STATUS_CHIP_STYLES = {
  /** Active / Complete / Matched / Collected — green family */
  success: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  },

  /** Pending / Partial / In-progress — orange family */
  warning: {
    backgroundColor: "#fff8e1",
    color: "#e65100",
  },

  /** Deactivated / Incomplete / Conflict / Failed — red family */
  error: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },

  /** Not-checked-in / Unmatched / Unknown / Neutral — grey family */
  default: {
    backgroundColor: "#f5f5f5",
    color: "#616161",
  },

  /** Not started / no REDCap form data — grey outlined family */
  noData: {
    backgroundColor: "#ffffff",
    color: "#757575",
    border: "1px solid #bdbdbd",
  },

  /** Informational — Follow-Up type labels, language tags — blue family */
  info: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
  },
};

/**
 * Maps a raw status string to the appropriate STATUS_CHIP_STYLES key.
 * Extend this map as new statuses are introduced.
 *
 * @param {string} status
 * @returns {object} sx color object from STATUS_CHIP_STYLES
 */
export const getStatusChipStyle = (status) => {
  if (!status) return STATUS_CHIP_STYLES.default;
  const normalized = String(status).trim().toUpperCase().replace(/ /g, "_");

  const map = {
    // Success variants
    ACTIVE: STATUS_CHIP_STYLES.success,
    COMPLETE: STATUS_CHIP_STYLES.success,
    MATCHED: STATUS_CHIP_STYLES.success,
    COMPLETED: STATUS_CHIP_STYLES.success,
    SUCCESS: STATUS_CHIP_STYLES.success,
    AVAILABLE: STATUS_CHIP_STYLES.success,
    SIGNED: STATUS_CHIP_STYLES.success,
    FINISHED: STATUS_CHIP_STYLES.success,
    CONFIRMED: STATUS_CHIP_STYLES.success,
    IN_CLINIC: STATUS_CHIP_STYLES.success,
    MITRABOX_RETURNED: STATUS_CHIP_STYLES.success,
    UPDATED: STATUS_CHIP_STYLES.success,
    RECEIVED: STATUS_CHIP_STYLES.success,
    SAMPLE_COLLECTED: STATUS_CHIP_STYLES.success,
    APPROVED: STATUS_CHIP_STYLES.success,
    NORMAL: STATUS_CHIP_STYLES.success,

    // Warning variants
    MILD_SYMPTOM: STATUS_CHIP_STYLES.warning,
    PARTIAL: STATUS_CHIP_STYLES.warning,
    PENDING: STATUS_CHIP_STYLES.warning,
    PENDING_MAIL: STATUS_CHIP_STYLES.warning,
    AMBIGUOUS: STATUS_CHIP_STYLES.warning,
    UNVERIFIED: STATUS_CHIP_STYLES.warning,
    NOT_CONTACTED: STATUS_CHIP_STYLES.warning,
    NONE: STATUS_CHIP_STYLES.warning,
    READY: STATUS_CHIP_STYLES.warning,
    UNKNOWN: STATUS_CHIP_STYLES.warning,
    NOT_CHECKED_IN: STATUS_CHIP_STYLES.warning,
    RUNNING: STATUS_CHIP_STYLES.warning,
    QUEUED: STATUS_CHIP_STYLES.warning,
    SKIPPED: STATUS_CHIP_STYLES.warning,

    // Error variants
    DEACTIVATED: STATUS_CHIP_STYLES.error,
    INCOMPLETE: STATUS_CHIP_STYLES.error,
    CONFLICT: STATUS_CHIP_STYLES.error,
    FAILED: STATUS_CHIP_STYLES.error,
    TIMED_OUT: STATUS_CHIP_STYLES.error,
    CANCELLED: STATUS_CHIP_STYLES.error,
    CONTACT_FAILED: STATUS_CHIP_STYLES.error,
    MISSED: STATUS_CHIP_STYLES.error,
    REJECTED: STATUS_CHIP_STYLES.error,
    ALZHEIMERS: STATUS_CHIP_STYLES.error,

    // Info variants
    FOLLOW_UP: STATUS_CHIP_STYLES.info,
    MAILED: STATUS_CHIP_STYLES.info,
    EMAIL_SENT: STATUS_CHIP_STYLES.info,
    SMS_SENT: STATUS_CHIP_STYLES.info,
    CALL_DONE: STATUS_CHIP_STYLES.info,
    MITRABOX_MAILED: STATUS_CHIP_STYLES.info,
    IN_PROGRESS: STATUS_CHIP_STYLES.info,

    // Reserved - use warning (orange) style
    RESERVED: STATUS_CHIP_STYLES.warning,

    // Default variants (explicit mappings)
    INACTIVE: STATUS_CHIP_STYLES.default,
    NO: STATUS_CHIP_STYLES.default,
    PLANNED: STATUS_CHIP_STYLES.default,
    USED: STATUS_CHIP_STYLES.default,  // For collection kits that are fully used

    // Success variants (additional)
    YES: STATUS_CHIP_STYLES.success,

    // No data variants
    NO_DATA: STATUS_CHIP_STYLES.noData,
  };

  return map[normalized] || STATUS_CHIP_STYLES.default;
};
